/**
 * `<caos-app-launcher>` — the platform App Launcher (shell region `a`, the LEADING control
 * of the workspace-tab row), reconstructing the reference application's `AppLauncher` layout recolored to
 * the Atlantis `--caos-*` tokens.
 * (Catalog: Navigation › AppLauncher — the leading control that lists the apps a person may open.)
 *
 * Trigger: JUST a waffle grid-icon button (the reference `.os-cmd-trigger` shape — bordered, subtle
 * bg, icon always in the accent). It carries NO app label or ◆ mark — the active app is
 * represented by its TABS in the workspace row, not by a label on this button (reference parity).
 * The current app is still tracked (`active-app-key`) so the PANEL marks its row "CURRENT".
 *
 * Panel: portaled to `document.body` (its own shadow host, so the workspace row's overflow /
 * stacking context can never clip or paint over it) and `position: fixed`, anchored under the
 * trigger from the trigger's live `getBoundingClientRect`. ~320px wide, token surface bg, 1px
 * border, radius, float shadow. Contents mirror the reference: a search box ("Search apps and
 * objects…") that filters the list, an "APPS" section label (mono, uppercase, wide-tracked),
 * app rows (icon + label + optional destination count) where the ACTIVE app row gets a
 * `--caos-color-surface-2` tint + an accent inset bar (`inset 3px 0 0`) + a "CURRENT" marker.
 * The list ends with the apps and their objects: App Manager is reached from Setup, not from here.
 *
 * Behavior (reference parity): toggles from the trigger; closes on outside-click, Escape, and
 * selection; repositions on scroll (capture) / resize.
 *
 * Data in: the `apps` PROPERTY (array — cannot ride an attribute) of
 * `{ key, label, icon?, brand?, destinationCount }`; the `active-app-key` attribute marks the
 * current app (its label/icon for the inline trigger are read from the matching `apps` entry).
 *
 * Events out (bubbling + composed — the host has no navigation/session logic):
 *   `launcher-get-more-apps` — the persistent "Get more apps" foot affordance was chosen
 *   `launcher-navigate` — an app or one of its objects was chosen; detail `{ key }` for an app,
 *                         `{ key, href }` for an object
 */
import { CaosElement, RESET, define, makeSheet } from '../base-element.js';
import { panelFocus, type PanelFocus } from '../focus.js';
import { anchorPanel } from '../anchored-panel.js';
import { makeIcon, isIconName } from './icons.js';

export interface CaosLauncherChild {
  key: string;
  label: string;
  href: string;
}

export interface CaosLauncherApp {
  key: string;
  label: string;
  icon?: string;
  brand?: string;
  destinationCount: number;
  /**
   * Optional object children shown INDENTED beneath the app row (reference parity: Sales
   * expands to Accounts · Contacts · Projects · Orders). Rendered only when there
   * is no active search query. Each child navigates via the SAME `launcher-navigate` event
   * as an app row, carrying its `href`. Populated by the host — the component just renders
   * the field if present.
   */
  children?: CaosLauncherChild[];
}

/**
 * CAOS-602 — the waffle. It was four rounded squares drawn here, at the same coordinates as the
 * vocabulary's `layout-grid`, in a file that was already importing that vocabulary for its search
 * glyph. Two marks from two places in one component is how a set stops being one set.
 */
const waffleIcon = (): SVGSVGElement => makeIcon('layout-grid');

const sheet = makeSheet(`
  :host { display: inline-flex; align-items: center; padding-left: var(--caos-space-5); }

  /* ── Trigger — the reference .os-cmd-trigger: JUST the waffle. Padding-driven (8px 14px), 1px
     border on the surface band, subtle bg, tight 2px radius; the waffle icon is always accent.
     No active-app label or ◆ mark — the app is represented by its TABS, not a button label. ── */
  .trigger {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 8px 14px;
    border: 1px solid var(--caos-color-border);
    background: var(--caos-color-surface-2);
    color: var(--caos-color-accent);
    line-height: 1;
    border-radius: var(--caos-radius-sm);
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease;
  }
  .trigger:hover { border-color: var(--caos-color-border-strong); background: var(--caos-color-surface); }
  .trigger:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); border-color: var(--caos-color-accent); }
  .trigger .waffle { width: 16px; height: 16px; color: var(--caos-color-accent); flex-shrink: 0; }
`);

/** Shadow-root stylesheet for the portaled panel (body-hosted, so it can't be clipped). */
const panelSheet = makeSheet(`
  .panel {
    position: fixed;
    /* A NAMED layer, not a number — see the header note on layering (CAOS-586). */
    z-index: var(--caos-layer-dropdown);
    width: 320px;
    max-height: 70vh;
    overflow-y: auto;
    background: var(--caos-color-surface);
    border: 1px solid var(--caos-color-border);
    border-radius: 4px;
    box-shadow: 0 18px 48px rgba(0,0,0,0.42);
    font-family: var(--caos-font);
  }
  .panel[hidden] { display: none; }

  .search-wrap { padding: 10px; border-bottom: 1px solid var(--caos-color-border); }
  .search {
    display: flex;
    align-items: center;
    gap: 8px;
    background: var(--caos-color-bg);
    border: 1px solid var(--caos-color-border);
    border-radius: 3px;
    padding: 7px 10px;
  }
  .search svg { width: 14px; height: 14px; color: var(--caos-color-text-muted); flex-shrink: 0; }
  .search input {
    flex: 1;
    background: transparent;
    border: none;
    outline: none;
    color: var(--caos-color-text);
    font-family: inherit;
    font-size: 13px;
    min-width: 0;
  }
  .search input::placeholder { color: var(--caos-color-text-ghost); }

  .section {
    font-family: var(--caos-font-mono);
    font-size: 9px;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: var(--caos-color-text-muted);
    padding: 10px 14px 6px;
  }

  .row {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 9px 14px;
    background: transparent;
    border: none;
    cursor: pointer;
    color: var(--caos-color-text);
    font-family: inherit;
    font-size: 13px;
    text-align: left;
  }
  .row:hover { background: var(--caos-color-surface-2); }
  .row:focus-visible { outline: none; background: var(--caos-color-surface-2); box-shadow: var(--caos-focus-ring); }
  .row.is-active {
    background: var(--caos-color-surface-2);
    box-shadow: inset 3px 0 0 var(--caos-color-accent);
  }
  /* An object child row indented beneath its app (reference parity: Sales → Accounts · Contacts · …).
     A leading 4px round bullet instead of the app icon. */
  .row--child { padding-left: 38px; }
  .row__bullet {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: var(--caos-color-text-muted);
    flex-shrink: 0;
  }
  .row__icon { width: var(--caos-size-xs-height); flex-shrink: 0; color: var(--caos-color-text-muted); font-size: 14px;
    display: inline-flex; align-items: center; justify-content: center; }
  .row__icon svg { width: 15px; height: 15px; display: block; }
  .row.is-active .row__icon { color: var(--caos-color-accent); }
  /* CAOS-287 — the "Get more apps" foot affordance. Separated by a rule so it reads as an action
     ON the list rather than another entry IN it, and muted so it never competes with the apps. */
  .row--get-more { border-top: 1px solid var(--caos-color-border); margin-top: 4px; padding-top: 10px; }
  .row--get-more .row__label { color: var(--caos-color-text-muted); }
  .row--get-more:hover .row__label { color: var(--caos-color-text); }
  .row__label { flex: 1; min-width: 0; }
  .row.is-active .row__label { font-weight: 600; }
  .row__meta { font-size: 12px; color: var(--caos-color-text-muted); white-space: nowrap; }
  .row__current {
    font-family: var(--caos-font-mono);
    font-size: 8px;
    letter-spacing: 0.14em;
    color: var(--caos-color-accent);
    white-space: nowrap;
  }

  .empty { padding: 16px 14px; color: var(--caos-color-text-muted); font-size: 13px; }
`);

// CAOS-634 — module-level counter so the search input's id stays unique even if more than one
// launcher instance ends up on the same page (defensive; today there is exactly one, in the
// workspace tabs' launcher slot).
let uid = 0;

export class CaosAppLauncher extends CaosElement {
  static get observedAttributes(): string[] {
    return ['active-app-key'];
  }

  // CAOS-634 — gives the search input a stable, unique id (see below) so it stops showing up as
  // an unidentified form field in browser diagnostics; it already has an aria-label.
  private readonly seq = ++uid;

  private _apps: CaosLauncherApp[] = [];
  private _query = '';
  private open = false;

  private trigger!: HTMLButtonElement;

  // The panel lives in its OWN shadow host appended to <body> (portaled), so no ancestor's
  // overflow clip or stacking context can swallow it. Both roots adopt the shared reset.
  private portalHost: HTMLDivElement | null = null;
  /** CAOS-651 — focus into the open panel, kept there, and handed back on close. */
  private panelFocus: PanelFocus | null = null;
  private portalRoot: ShadowRoot | null = null;
  private panel!: HTMLElement;
  private input!: HTMLInputElement;
  private list!: HTMLElement;

  private readonly onDocPointer = (e: MouseEvent): void => {
    if (!this.open) return;
    const path = e.composedPath();
    if (path.includes(this)) return;
    if (this.portalHost && path.includes(this.portalHost)) return;
    this.setOpen(false);
  };
  private readonly onDocKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') this.setOpen(false, true);
    else this.panelFocus?.trap(e);
  };
  private readonly reposition = (): void => {
    if (!this.open) return;
    // CAOS-659 — under the trigger, lined up with its left edge; above it when there is no room
    // below; slid along to stay inside the visible viewport either way.
    anchorPanel(this.panel, this.trigger, { side: 'bottom', align: 'start', gap: 8 });
  };

  constructor() {
    super(sheet);

    this.trigger = document.createElement('button');
    this.trigger.type = 'button';
    this.trigger.className = 'trigger';
    this.trigger.setAttribute('aria-haspopup', 'menu');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.trigger.setAttribute('aria-label', 'App Launcher');

    const waffle = waffleIcon();
    waffle.classList.add('waffle');
    this.trigger.append(waffle);

    this.trigger.addEventListener('click', () => this.setOpen(!this.open));
    this.root.append(this.trigger);
  }

  connectedCallback(): void {
    this.ensurePortal();
    this.syncTrigger();
    this.renderList();
  }

  disconnectedCallback(): void {
    document.removeEventListener('mousedown', this.onDocPointer);
    document.removeEventListener('keydown', this.onDocKey);
    window.removeEventListener('scroll', this.reposition, true);
    window.removeEventListener('resize', this.reposition);
    this.portalHost?.remove();
    this.portalHost = null;
    this.portalRoot = null;
    this.panelFocus = null;
  }

  attributeChangedCallback(): void {
    this.syncTrigger();
    this.renderList();
  }

  /** The apps to list (data in — an array, so a property not an attribute). */
  get apps(): CaosLauncherApp[] {
    return this._apps;
  }
  set apps(list: CaosLauncherApp[]) {
    this._apps = Array.isArray(list) ? list : [];
    this.syncTrigger();
    this.renderList();
  }

  private get activeKey(): string | null {
    const k = this.getAttribute('active-app-key');
    return k && k.trim() ? k.trim() : null;
  }

  /** Build the body-hosted portal shadow root that carries the panel. */
  private ensurePortal(): void {
    if (this.portalHost) return;
    this.portalHost = document.createElement('div');
    this.portalHost.className = 'caos-app-launcher-portal';
    this.portalRoot = this.portalHost.attachShadow({ mode: 'open' });
    // RESET first, exactly as the base class does for the component's own root: this root is
    // outside that machinery, so without this line the shared hiding guard would not reach
    // anything inside the panel. See base-element.ts.
    this.portalRoot.adoptedStyleSheets = [RESET, panelSheet];

    this.panel = document.createElement('div');
    this.panel.className = 'panel';
    this.panel.setAttribute('role', 'menu');
    this.panel.setAttribute('aria-label', 'Apps');
    this.panel.hidden = true;

    const searchWrap = document.createElement('div');
    searchWrap.className = 'search-wrap';
    const search = document.createElement('div');
    search.className = 'search';
    search.append(makeIcon('search'));
    this.input = document.createElement('input');
    this.input.type = 'text';
    this.input.id = `caos-app-launcher-search-${this.seq}`;
    this.input.placeholder = 'Search apps and objects…';
    this.input.setAttribute('aria-label', 'Search apps and objects');
    this.input.addEventListener('input', () => {
      this._query = this.input.value;
      this.renderList();
    });
    search.append(this.input);
    searchWrap.append(search);

    this.list = document.createElement('div');
    this.list.className = 'list';

    this.panel.append(searchWrap, this.list);
    this.portalRoot.append(this.panel);
    document.body.append(this.portalHost);
  }

  /** `restoreFocus` — Escape or a choice hands focus back to the trigger; an outside click does not. */
  private setOpen(open: boolean, restoreFocus = false): void {
    if (open === this.open) return;
    this.open = open;
    this.ensurePortal();
    this.panel.hidden = !open;
    this.trigger.setAttribute('aria-expanded', String(open));
    if (open) {
      this._query = '';
      this.input.value = '';
      this.renderList();
      this.reposition();
      document.addEventListener('mousedown', this.onDocPointer);
      document.addEventListener('keydown', this.onDocKey);
      window.addEventListener('scroll', this.reposition, true);
      window.addEventListener('resize', this.reposition);
      // focus the search after paint — unless the panel was closed again before that frame
      const focus = panelFocus(this.panel);
      this.panelFocus = focus;
      requestAnimationFrame(() => {
        if (this.open && this.panelFocus === focus) focus.open({ opener: this.trigger, initial: this.input });
      });
    } else {
      document.removeEventListener('mousedown', this.onDocPointer);
      document.removeEventListener('keydown', this.onDocKey);
      window.removeEventListener('scroll', this.reposition, true);
      window.removeEventListener('resize', this.reposition);
      this.panelFocus?.close(restoreFocus);
      this.panelFocus = null;
    }
  }

  private emit(type: string, detail?: unknown): void {
    this.dispatchEvent(new CustomEvent(type, { bubbles: true, composed: true, detail }));
  }

  /** The trigger is JUST the waffle — it carries no app label or mark. The active app is still
   *  tracked (via `active-app-key`) so the PANEL marks the current app row; the button only reflects
   *  it in its accessible name/tooltip. */
  private syncTrigger(): void {
    const active = this.activeKey ? this._apps.find((a) => a.key === this.activeKey) : undefined;
    if (active) {
      this.trigger.setAttribute('aria-label', `App Launcher — current app: ${active.label}`);
      this.trigger.title = `App Launcher · ${active.label}`;
    } else {
      this.trigger.setAttribute('aria-label', 'App Launcher');
      this.trigger.title = 'App Launcher';
    }
  }

  private renderList(): void {
    if (!this.portalRoot) return;
    this.list.replaceChildren();
    const q = this._query.trim().toLowerCase();

    // Apps whose OWN label matches the query (or every app when there is no query) — these
    // render as app rows in the "Apps" section.
    const appRows = this._apps.filter((a) => !q || a.label.toLowerCase().includes(q));

    // When a query is active, ALSO surface matching object CHILDREN whose parent app's label
    // does NOT itself match — as a flat hits list under a section headed by the parent app's
    // label (reference parity: searching "Accounts"/"Orders" surfaces them under the "Sales"
    // header even though "Sales" does not contain the query). Only children are matched here;
    // an app whose own label matched is already shown as an app row above.
    const childHitGroups = q
      ? this._apps
          .filter((a) => !a.label.toLowerCase().includes(q))
          .map((a) => ({ app: a, hits: (a.children ?? []).filter((c) => c.label.toLowerCase().includes(q)) }))
          .filter((g) => g.hits.length > 0)
      : [];

    // Empty only when there are no app rows AND no object child hits (reference parity).
    if (appRows.length === 0 && childHitGroups.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'empty';
      empty.textContent = q ? `No apps or objects match “${this._query}”.` : 'No apps yet.';
      this.list.append(empty);
      // CAOS-287 — still offered here, and this is the case that matters most: a search that
      // matched nothing is exactly when the app somebody wants may not be installed yet.
      this.appendGetMore();
      return;
    }

    if (appRows.length > 0) {
      const section = document.createElement('div');
      section.className = 'section';
      section.textContent = 'Apps';
      this.list.append(section);

      for (const a of appRows) {
        const isActive = a.key === this.activeKey;
        const row = document.createElement('button');
        row.type = 'button';
        row.className = isActive ? 'row is-active' : 'row';
        row.setAttribute('role', 'menuitem');
        if (isActive) row.setAttribute('aria-current', 'true');

        const icon = document.createElement('span');
        icon.className = 'row__icon';
        icon.setAttribute('aria-hidden', 'true');
        // CAOS-378 — an app names a glyph from the one vocabulary and it is DRAWN, so it inherits the
        // row's colour, sits on the same optical baseline as every other mark, and looks like the rest
        // of the workspace. A character (an emoji, a ◆) is a font's opinion, not the design system's:
        // it lands at a different weight and size on every machine. A name we do not draw still falls
        // back to the character, so an app from someone else's package is never blank.
        if (a.icon && isIconName(a.icon)) icon.append(makeIcon(a.icon));
        else icon.textContent = a.icon ?? '◆';
        if (a.brand && !isActive) icon.style.color = a.brand;

        const label = document.createElement('span');
        label.className = 'row__label';
        label.textContent = a.label;

        row.append(icon, label);

        if (isActive) {
          const marker = document.createElement('span');
          marker.className = 'row__current';
          marker.textContent = 'CURRENT';
          row.append(marker);
        }

        row.addEventListener('click', () => {
          this.emit('launcher-navigate', { key: a.key });
          this.setOpen(false, true);
        });
        this.list.append(row);

        // Object children indented beneath the app row (reference parity: Sales → Accounts ·
        // Contacts · Projects · Orders). Only when there is NO active query — a search
        // surfaces object hits its own way (the flat child-hit groups below). Each child reuses
        // the same `launcher-navigate` event as an app row, carrying its `href`.
        if (!q && a.children && a.children.length > 0) {
          for (const c of a.children) {
            const child = document.createElement('button');
            child.type = 'button';
            child.className = 'row row--child';
            child.setAttribute('role', 'menuitem');

            const bullet = document.createElement('span');
            bullet.className = 'row__bullet';
            bullet.setAttribute('aria-hidden', 'true');

            const childLabel = document.createElement('span');
            childLabel.className = 'row__label';
            childLabel.textContent = c.label;

            child.append(bullet, childLabel);
            child.addEventListener('click', () => {
              this.emit('launcher-navigate', { key: c.key, href: c.href });
              this.setOpen(false, true);
            });
            this.list.append(child);
          }
        }
      }
    }

    // Search-only: matching object children whose parent app row did NOT match — surfaced as a
    // flat hits list under a section headed by the parent app's label (source: the "Sales"
    // section, one flat label row per matching object). Non-indented plain rows (no bullet), the
    // closest match to the source's flat object rows.
    for (const g of childHitGroups) {
      const section = document.createElement('div');
      section.className = 'section';
      section.textContent = g.app.label;
      this.list.append(section);

      for (const c of g.hits) {
        const row = document.createElement('button');
        row.type = 'button';
        row.className = 'row';
        row.setAttribute('role', 'menuitem');

        const label = document.createElement('span');
        label.className = 'row__label';
        label.textContent = c.label;
        row.append(label);

        row.addEventListener('click', () => {
          this.emit('launcher-navigate', { key: c.key, href: c.href });
          this.setOpen(false, true);
        });
        this.list.append(row);
      }
    }
    // No "App Manager…" foot row — the reference launcher ends after the apps/objects; App Manager is
    // reached from Setup, not the launcher.
    this.appendGetMore();
  }

  /**
   * CAOS-287 — the "Get more apps" foot affordance.
   *
   * Parity with the reference launcher's "Visit AppExchange / Get more apps" link at the foot of
   * the panel, and the answer to where an in-shell install surface lives: somebody who wants
   * another app is already opening the launcher to look for it, so the place they look is the
   * place it is offered. A dedicated Setup surface is the natural SECOND front door; it is not
   * this ticket, and both would drive the same install path.
   *
   * The component only says it was chosen. It opens nothing itself and knows nothing about
   * packages, catalogs or installs — the host owns that, the same way it owns navigation. That is
   * what lets this element stay renderable in a page with no install seam at all.
   */
  private appendGetMore(): void {
    const row = document.createElement('button');
    row.type = 'button';
    row.className = 'row row--get-more';
    row.dataset.action = 'get-more-apps';
    row.setAttribute('role', 'menuitem');

    const icon = document.createElement('span');
    icon.className = 'row__icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = '+';

    const label = document.createElement('span');
    label.className = 'row__label';
    label.textContent = 'Get more apps';

    row.append(icon, label);
    row.addEventListener('click', () => {
      this.emit('launcher-get-more-apps');
      this.setOpen(false, true);
    });
    this.list.append(row);
  }
}

define('caos-app-launcher', CaosAppLauncher);

declare global {
  interface HTMLElementTagNameMap {
    'caos-app-launcher': CaosAppLauncher;
  }
}
